import React from "react";
import { FiTruck, FiGift } from "react-icons/fi";
import { BsCheckCircleFill } from "react-icons/bs";
import { getParsedLocalStorageItem } from "../Navbar/Navbar";

const FONT = "'Plus Jakarta Sans', sans-serif";

// Design tokens (from cart redesign)
const COLORS = {
  ink: "#23272f",
  muted: "#7c8593",
  line: "#eceef0",
  card: "#ffffff",
  amber: "#f0a52b",
  amberTrack: "#f6e6c4",
  giftBg: "#e3efd6",
  giftGreen: "#4f8a3d",
  giftTrack: "#cbe0b6",
};

const CartIncentive = ({
  totalAmount = 0,
  showFreeDelivery = true,
  isMobile = false,
}) => {
  const userData = getParsedLocalStorageItem("userData");
  const belongsToPartner = userData?.belongsToPartner === true;

  const FREE_DELIVERY_THRESHOLD = 200000;
  const FREE_GIFT_THRESHOLD = 120000;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const deliveryRemaining = Math.max(0, FREE_DELIVERY_THRESHOLD - totalAmount);
  const giftRemaining = Math.max(0, FREE_GIFT_THRESHOLD - totalAmount);

  const deliveryProgress = Math.min(
    100,
    (totalAmount / FREE_DELIVERY_THRESHOLD) * 100,
  );
  const giftProgress = Math.min(100, (totalAmount / FREE_GIFT_THRESHOLD) * 100);

  const deliveryUnlocked = totalAmount >= FREE_DELIVERY_THRESHOLD;
  const giftUnlocked = totalAmount >= FREE_GIFT_THRESHOLD;

  // Compact sizing on mobile so the cards don't compete with cart items
  const cardPadding = isMobile ? "6px 11px" : "13px 14px";
  const cardRadius = isMobile ? "12px" : "16px";
  const trackHeight = isMobile ? "4px" : "8px";
  const iconSize = isMobile ? 14 : 20;
  const checkSize = isMobile ? 13 : 18;

  const titleStyle = {
    fontSize: isMobile ? "11.5px" : "13.5px",
    fontWeight: 700,
    color: COLORS.ink,
    fontFamily: FONT,
  };
  const hintStyle = {
    marginTop: isMobile ? "3px" : "8px",
    marginBottom: 0,
    fontSize: isMobile ? "10px" : "12px",
    color: "#5e6472",
    fontWeight: 700,
    fontFamily: FONT,
    textAlign: "right",
  };

  return (
    <div
      className="d-flex flex-column w-100"
      style={{ gap: isMobile ? "6px" : "8px" }}
    >
      {/* Free Delivery Reward */}
      {showFreeDelivery && !belongsToPartner && (
        <div
          style={{
            background: COLORS.card,
            border: `1px solid ${COLORS.amber}`,
            borderRadius: cardRadius,
            padding: cardPadding,
          }}
        >
          {deliveryUnlocked ? (
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <BsCheckCircleFill size={checkSize} color={COLORS.amber} />
                <span style={titleStyle}>You've unlocked free delivery!</span>
              </div>
              <FiTruck size={iconSize} color={COLORS.amber} />
            </div>
          ) : (
            <>
              <div
                className="d-flex justify-content-between align-items-center"
                style={{ marginBottom: isMobile ? "4px" : "8px" }}
              >
                <b style={titleStyle}>Free delivery</b>
                <FiTruck size={iconSize} color={COLORS.amber} />
              </div>
              <div
                style={{
                  height: trackHeight,
                  borderRadius: "99px",
                  background: COLORS.amberTrack,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${deliveryProgress}%`,
                    borderRadius: "99px",
                    background: `linear-gradient(90deg, #f3b54e, ${COLORS.amber})`,
                    transition: "width 0.5s ease-in-out",
                  }}
                />
              </div>
              <p style={hintStyle}>
                Add{" "}
                <b style={{ color: COLORS.ink }}>
                  {formatCurrency(deliveryRemaining)}
                </b>{" "}
                more to unlock free delivery
              </p>
            </>
          )}
        </div>
      )}

      {/* Free Gift Reward */}
      <div
        style={{
          background: COLORS.giftBg,
          border: "1px solid #cfe3bf",
          borderRadius: cardRadius,
          padding: cardPadding,
        }}
      >
        {giftUnlocked ? (
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <BsCheckCircleFill size={checkSize} color={COLORS.giftGreen} />
              <span style={{ ...titleStyle, color: "#33502a" }}>
                You've earned a free gift!
              </span>
            </div>
            <FiGift size={iconSize} color={COLORS.giftGreen} />
          </div>
        ) : (
          <>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{ marginBottom: isMobile ? "5px" : "8px" }}
            >
              <b style={titleStyle}>Free gift</b>
              <FiGift size={iconSize} color={COLORS.giftGreen} />
            </div>
            <div
              style={{
                height: trackHeight,
                borderRadius: "99px",
                background: COLORS.giftTrack,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${giftProgress}%`,
                  borderRadius: "99px",
                  background: COLORS.giftGreen,
                  transition: "width 0.5s ease-in-out",
                }}
              />
            </div>
            <p style={hintStyle}>
              Add{" "}
              <b style={{ color: COLORS.ink }}>
                {formatCurrency(giftRemaining)}
              </b>{" "}
              more for a free gift
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default CartIncentive;
